/**
 * POS Receipt — opens a new window with a config-driven receipt HTML.
 *
 * Phase 2 makes every section of the receipt toggleable from
 * Settings → Print Formats → "POS Receipt". Defaults below reproduce the
 * legacy receipt exactly, so a caller that doesn't pass a settings object
 * still sees the same output as before the upgrade.
 */

import { formatCurrency, formatDate } from '../../utils/formatters';
import { isRawBTEnabled, printViaRawBT } from '../../utils/escposReceipt';
import { localDateStr } from '../../utils/localDate';
import { formatTwoUnitQty } from '../../utils/unitConversion';
import { isPrintBrandHidden, PRINT_BRAND_TEXT } from '../../utils/printBranding';

const DEFAULTS = {
  paperSize: '80mm',
  receiptTitle: 'Sales Receipt',
  showLogo: false,
  logoSize: 'md',
  showCompanyName: true,
  showAddress: true,
  showPhone: true,
  showNTN: true,
  showGST: true,
  showWebsite: true,
  showCashier: true,
  showCustomer: true,
  showItemCode: false,
  showUoM: true,
  showDiscountColumn: true,
  showTaxBreakdown: true,
  showSubtotal: true,
  showPaymentMethod: true,
  showTenderedChange: true,
  showPreviousBalance: false,
  showBarcode: false,
  headerText: '',
  footerText: 'Thank you for your purchase!',
  promoMessage: '',
  returnPolicyText: '',
  fontSize: 'medium',
  boldTotal: true,
  copies: 1,
  currencyPosition: 'before',
};

const PAPER_CSS = {
  '58mm': { width: '58mm', padding: '4px 6px', baseFont: 12 },
  // Content is kept a touch narrower than the 80mm paper so the right-aligned
  // amount column clears the printer's physical right margin (was clipping).
  '80mm': { width: '74mm', padding: '6px 8px', baseFont: 13 },
  'A5':   { width: '148mm', padding: '12px 14px', baseFont: 13 },
  'A4':   { width: '210mm', padding: '18px 22px', baseFont: 13 },
};

const FONT_DELTA = { small: -1, medium: 0, large: 2 };
const LOGO_PX = { sm: 36, md: 56, lg: 80 };

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const fmt = (cfg, v) => {
  const n = formatCurrency(v || 0);
  // formatCurrency already wraps the value with the configured currency symbol.
  // The position toggle here lets POS receipts swap the symbol to the trailing
  // side without changing the global currency setting (cosmetic only).
  if (cfg.currencyPosition === 'after') {
    // Strip a leading non-digit run and append it as a trailing suffix.
    const m = String(n).match(/^([^0-9\-]*)([\s\S]*)$/);
    if (m && m[1].trim()) return `${m[2].trim()} ${m[1].trim()}`;
  }
  return n;
};

export const printReceipt = (transactionData, activeCompany, posReceiptSettings = null) => {
  const cfg = { ...DEFAULTS, ...(posReceiptSettings || {}) };

  // Per-device Bluetooth thermal path (mobile POS via RawBT). Only when THIS
  // device has the flag on — desktop tills fall straight through to the browser
  // print below, completely unchanged. Synchronous so the rawbt: app-launch
  // stays inside the PAY tap gesture.
  if (isRawBTEnabled()) {
    try { printViaRawBT(transactionData, activeCompany, cfg); }
    catch (err) { console.warn('RawBT print failed:', err); }
    return;
  }

  const paper = PAPER_CSS[cfg.paperSize] || PAPER_CSS['80mm'];
  const baseFont = paper.baseFont + (FONT_DELTA[cfg.fontSize] ?? 0);

  const {
    docNumber, docDate,
    lines = [], cart = [],
    subtotal, discountAmount, taxAmount, totalAmount,
    paymentMethod, payments, cashTendered, changeGiven,
    customerName, cashierName,
    previousBalance,  // customer's outstanding BEFORE this bill (null for walk-in / when off)
    bankDiscount,     // { label, percent, amount, cardMask } — POS bank card offer (optional)
    exchange  // { isExchange, originalDocNumber, returnDocNumber, returnLines, returnSubtotal, reason, netAmount }
  } = transactionData;

  const receiptLines = cart.length > 0 ? cart : lines;
  const dateStr = formatDate(docDate || localDateStr());
  const timeStr = new Date().toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });

  // ─── Header block ─────────────────────────────────────────────────────
  const logoUrl = cfg.showLogo ? (activeCompany?.logoUrl || activeCompany?.brandLogoUrl) : null;
  const logoSize = LOGO_PX[cfg.logoSize] || LOGO_PX.md;
  const logoHtml = (cfg.showLogo && logoUrl)
    ? `<div class="logo-wrap"><img src="${esc(logoUrl)}" alt="logo" style="max-height:${logoSize}px;max-width:90%;object-fit:contain;"/></div>`
    : '';

  const companyNameHtml = cfg.showCompanyName
    ? `<div class="company-name">${esc(activeCompany?.companyName || 'Store')}</div>` : '';
  const addressHtml = (cfg.showAddress && activeCompany?.address)
    ? `<div class="company-address">${esc(activeCompany.address)}</div>` : '';

  const contactBits = [];
  if (cfg.showPhone && activeCompany?.phone) contactBits.push('Tel: ' + esc(activeCompany.phone));
  if (cfg.showNTN && activeCompany?.ntn) {
    const label = (activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN';
    contactBits.push(`${label}: ${esc(activeCompany.ntn)}`);
  }
  if (cfg.showGST && activeCompany?.gst) {
    contactBits.push(`${esc(activeCompany?.taxLabel || 'GST')}: ${esc(activeCompany.gst)}`);
  }
  if (cfg.showWebsite && activeCompany?.website) contactBits.push(esc(activeCompany.website));
  const contactHtml = contactBits.length ? `<div class="company-contact">${contactBits.join('<br>')}</div>` : '';

  const headerCustomTextHtml = cfg.headerText
    ? `<div class="header-text">${esc(cfg.headerText)}</div>` : '';

  // ─── Meta rows ────────────────────────────────────────────────────────
  const metaRows = [];
  metaRows.push(`<div class="header-row"><span>Receipt#</span><span>${esc(docNumber || '')}</span></div>`);
  metaRows.push(`<div class="header-row"><span>Date</span><span>${esc(dateStr)} ${esc(timeStr)}</span></div>`);
  if (cfg.showCashier && cashierName) {
    metaRows.push(`<div class="header-row"><span>Cashier</span><span>${esc(cashierName)}</span></div>`);
  }
  if (cfg.showCustomer && customerName && customerName !== 'Walk-in Customer') {
    metaRows.push(`<div class="header-row"><span>Customer</span><span>${esc(customerName)}</span></div>`);
  }

  // ─── Item rows ────────────────────────────────────────────────────────
  const itemRows = receiptLines.map(item => {
    const name = item.itemName || item.description || '';
    const code = cfg.showItemCode && item.itemCode ? ` <span class="item-code">[${esc(item.itemCode)}]</span>` : '';
    // Show Two Units: a line saved with a second unit prints both units,
    // e.g. "14.4 Mtr x 100" then "= 10 Ctn".
    const two = item.secondUnitCode && item.secondQuantity != null;
    const qty = two ? formatTwoUnitQty(item.quantity || 0) : (item.quantity || 0);
    const uom = two ? (item.unitCode || item.unitOfMeasure || '') : (cfg.showUoM ? (item.unitOfMeasure || '') : '');
    const rate = item.unitPrice || item.priceExclTax || 0;
    const amount = item.lineTotal || ((item.quantity || 0) * rate);
    const discPct = Number(item.discountPercent) || 0;
    const discAmt = Number(item.discountAmount) || 0;
    const discText = cfg.showDiscountColumn && (discPct > 0 || discAmt > 0)
      ? `<div class="item-disc">${discPct > 0 ? `Disc ${discPct}%` : `Disc ${fmt(cfg, discAmt)}`}</div>` : '';
    const twoText = two ? `<div class="item-disc" style="color:#333">= ${formatTwoUnitQty(item.secondQuantity)} ${esc(item.secondUnitCode)}</div>` : '';
    return `
      <div class="item-name">${esc(name)}${code}</div>
      <div class="item-detail">
        <span>${qty}${uom ? ' ' + esc(uom) : ''} x ${fmt(cfg, rate)}</span>
        <span>${fmt(cfg, amount)}</span>
      </div>
      ${twoText}
      ${discText}
    `;
  }).join('');

  // ─── Totals block ─────────────────────────────────────────────────────
  const totalsRows = [];
  if (cfg.showSubtotal) {
    totalsRows.push(`<div class="total-row"><span>Subtotal</span><span>${fmt(cfg, subtotal || 0)}</span></div>`);
  }
  const bankDisc = Number(bankDiscount?.amount) || 0;
  if (bankDisc > 0) {
    // Bank card offer shown on its own line; "Discount" keeps only the rest.
    const otherDisc = Math.round(((Number(discountAmount) || 0) - bankDisc) * 100) / 100;
    if (otherDisc > 0.004) {
      totalsRows.push(`<div class="total-row"><span>Discount</span><span>-${fmt(cfg, otherDisc)}</span></div>`);
    }
    totalsRows.push(`<div class="total-row"><span>${esc(bankDiscount.label || 'Bank')} ${esc(String(bankDiscount.percent ?? ''))}% Disc</span><span>-${fmt(cfg, bankDisc)}</span></div>`);
    if (bankDiscount.cardMask) {
      totalsRows.push(`<div class="total-row"><span>Card</span><span>${esc(bankDiscount.cardMask)}</span></div>`);
    }
  } else if (discountAmount > 0) {
    totalsRows.push(`<div class="total-row"><span>Discount</span><span>-${fmt(cfg, discountAmount)}</span></div>`);
  }
  if (cfg.showTaxBreakdown && taxAmount > 0) {
    totalsRows.push(`<div class="total-row"><span>Tax</span><span>${fmt(cfg, taxAmount)}</span></div>`);
  }
  const totalsHtml = totalsRows.length ? `<div class="total-section">${totalsRows.join('')}</div>` : '';

  // ─── Exchange block (Phase 2) ─────────────────────────────────────────
  // When the sale is part of an Exchange, we print the return half above the
  // payment block so the customer sees: new items → return credit → net.
  let exchangeHtml = '';
  if (exchange && exchange.isExchange) {
    const retRows = (exchange.returnLines || []).map(item => {
      const qty = item.quantity || 0;
      const rate = item.unitPrice || 0;
      const amt = item.lineTotal || qty * rate;
      const uom = cfg.showUoM ? (item.unitOfMeasure || '') : '';
      return `
        <div class="item-name return-name">${esc(item.itemName || '')} <span class="return-tag">RETURN</span></div>
        <div class="item-detail">
          <span>${qty}${uom ? ' ' + esc(uom) : ''} x ${fmt(cfg, rate)}</span>
          <span>-${fmt(cfg, amt)}</span>
        </div>
      `;
    }).join('');
    const net = Number(exchange.netAmount) || 0;
    const netLabel = net > 0.01 ? 'NET DUE' : net < -0.01 ? 'REFUND DUE' : 'EVEN EXCHANGE';
    const netVal = fmt(cfg, Math.abs(net));
    exchangeHtml = `
      <div class="divider"></div>
      <div class="exchange-block">
        <div class="exchange-title">EXCHANGE · Against ${esc(exchange.originalDocNumber || '')}${exchange.returnDocNumber ? ' · Return ' + esc(exchange.returnDocNumber) : ''}</div>
        ${exchange.reason ? `<div class="exchange-reason">Reason: ${esc(exchange.reason)}</div>` : ''}
        ${retRows}
        <div class="total-row exchange-line"><span>Return credit</span><span>-${fmt(cfg, exchange.returnSubtotal || 0)}</span></div>
        <div class="exchange-net"><span>${netLabel}</span><span>${netVal}</span></div>
      </div>
    `;
  }

  // ─── Payment block ────────────────────────────────────────────────────
  let paymentHtml = '';
  if (cfg.showPaymentMethod) {
    let paymentInfo = '';
    if (payments && payments.length > 0) {
      paymentInfo = payments
        .filter(p => p.amount > 0)
        .map(p => `<div class="pay-line"><span>${esc((p.method || '').toUpperCase())}</span><span>${fmt(cfg, p.amount)}</span></div>`)
        .join('');
    } else if (totalAmount > 0) {
      paymentInfo = `<div class="pay-line"><span>${esc((paymentMethod || 'CASH').toUpperCase())}</span><span>${fmt(cfg, totalAmount)}</span></div>`;
    }
    if (cfg.showTenderedChange && cashTendered > 0) {
      paymentInfo += `<div class="pay-line"><span>Tendered</span><span>${fmt(cfg, cashTendered)}</span></div>`;
      paymentInfo += `<div class="pay-line change"><span>Change</span><span>${fmt(cfg, changeGiven || 0)}</span></div>`;
    }
    if (paymentInfo) {
      paymentHtml = `<div class="payment-section"><div class="payment-title">Payment</div>${paymentInfo}</div>`;
    }
  }

  // ─── Previous / New balance block ─────────────────────────────────────
  // Only for named credit customers when the toggle is on. previousBalance is
  // the customer's outstanding BEFORE this bill; New = Previous + Bill − Paid,
  // where Paid counts only real tenders (a "credit" line is what goes on account).
  let balanceHtml = '';
  const prevBalNum = Number(previousBalance);
  const balCustomerOk = customerName && customerName !== 'Walk-in Customer';
  if (cfg.showPreviousBalance && previousBalance != null && !Number.isNaN(prevBalNum) && balCustomerOk) {
    const billTotal = Number(totalAmount) || 0;
    const paidThisSale = Math.min(
      (Array.isArray(payments) ? payments : [])
        .filter(p => String(p.method || '').toLowerCase() !== 'credit')
        .reduce((s, p) => s + (Number(p.amount) || 0), 0),
      billTotal
    );
    const newBalNum = prevBalNum + billTotal - paidThisSale;
    balanceHtml = `
      <div class="balance-section">
        <div class="balance-title">Account Balance</div>
        <div class="total-row"><span>Previous Balance</span><span>${fmt(cfg, prevBalNum)}</span></div>
        <div class="total-row"><span>This Bill</span><span>${fmt(cfg, billTotal)}</span></div>
        <div class="total-row"><span>Paid</span><span>-${fmt(cfg, paidThisSale)}</span></div>
        <div class="total-row balance-new"><span>New Balance</span><span>${fmt(cfg, newBalNum)}</span></div>
      </div>
    `;
  }

  // ─── Barcode (optional) ───────────────────────────────────────────────
  // We load JsBarcode lazily from a CDN inside the print window. Offline = no
  // barcode but the receipt still prints fine (the text below remains visible).
  const barcodeHtml = (cfg.showBarcode && docNumber) ? `
    <div class="barcode-wrap">
      <svg id="receipt-barcode"></svg>
      <div class="barcode-text">${esc(docNumber)}</div>
    </div>
  ` : '';
  const barcodeScript = (cfg.showBarcode && docNumber) ? `
    var s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
    s.onload = function(){
      try { JsBarcode('#receipt-barcode', ${JSON.stringify(String(docNumber))}, {
        format: 'CODE128', width: 1.6, height: 38, displayValue: false, margin: 0
      }); } catch(e) {}
      setTimeout(printAll, 30);
    };
    s.onerror = function(){ setTimeout(printAll, 0); };
    document.head.appendChild(s);
  ` : 'setTimeout(printAll, 0);';

  // ─── Free text blocks ─────────────────────────────────────────────────
  const promoHtml = cfg.promoMessage
    ? `<div class="footer-promo">${esc(cfg.promoMessage)}</div>` : '';
  const policyHtml = cfg.returnPolicyText
    ? `<div class="footer-policy">${esc(cfg.returnPolicyText)}</div>` : '';
  const thanksHtml = cfg.footerText
    ? `<div class="footer-thanks">${esc(cfg.footerText)}</div>` : '';

  // ─── Build receipt body ───────────────────────────────────────────────
  const buildBody = () => `
    <div class="company-header">
      ${logoHtml}
      ${companyNameHtml}
      ${addressHtml}
      ${contactHtml}
    </div>

    ${headerCustomTextHtml}

    <div class="receipt-label">${esc(cfg.receiptTitle || 'Sales Receipt')}</div>

    <div class="header-section">
      ${metaRows.join('')}
    </div>

    <div style="margin-top: 8px;">
      <div class="items-header"><span>Item</span><span>Amount</span></div>
      ${itemRows}
    </div>

    <div class="divider"></div>

    ${totalsHtml}

    <div class="grand-total-section ${cfg.boldTotal ? 'bold' : ''}">
      <div class="grand-total"><span>TOTAL</span><span>${fmt(cfg, totalAmount || 0)}</span></div>
    </div>

    ${exchangeHtml}

    ${paymentHtml}

    ${balanceHtml}

    ${barcodeHtml}

    <div class="divider"></div>
    <div class="footer-section">
      ${thanksHtml}
      ${promoHtml}
      ${policyHtml}
      ${isPrintBrandHidden(activeCompany) ? '' : `<div class="footer-brand">${PRINT_BRAND_TEXT}</div>`}
    </div>
  `;

  const copies = Math.min(Math.max(Number(cfg.copies) || 1, 1), 5);
  const copiesHtml = Array.from({ length: copies }).map((_, i) =>
    `<div class="receipt-copy ${i > 0 ? 'cut' : ''}">${buildBody()}</div>`
  ).join('');

  // ─── Document HTML ────────────────────────────────────────────────────
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Receipt ${esc(docNumber || '')}</title>
  <style>
    @page { size: ${paper.width} auto; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: ${baseFont}px;
      width: ${paper.width};
      padding: ${paper.padding};
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .receipt-copy { page-break-after: auto; }
    .receipt-copy.cut { border-top: 2px dashed #000; margin-top: 8px; padding-top: 8px; }
    .center { text-align: center; }
    .divider {
      border: none;
      border-top: 2px dashed #000;
      margin: 8px 0;
    }
    .company-header { text-align: center; padding: 4px 0 6px; }
    .logo-wrap { display: flex; justify-content: center; margin-bottom: 4px; }
    .company-name {
      font-size: ${baseFont + 9}px;
      font-weight: 900;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .company-address {
      font-size: ${baseFont - 1}px;
      font-weight: 700;
      line-height: 1.5;
      margin-bottom: 2px;
    }
    .company-contact {
      font-size: ${baseFont - 2}px;
      font-weight: 600;
      line-height: 1.6;
    }
    .header-text {
      text-align: center;
      font-size: ${baseFont - 1}px;
      font-weight: 700;
      margin: 6px 0 0;
    }
    .receipt-label {
      text-align: center;
      font-size: ${baseFont + 1}px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin: 8px 0 4px;
    }
    .header-section {
      background: #f0f0f0;
      padding: 6px 8px;
      margin: 0 -8px;
    }
    .header-row {
      display: flex;
      justify-content: space-between;
      font-size: ${baseFont - 1}px;
      font-weight: 700;
      margin: 3px 0;
    }
    .header-row span:first-child { font-weight: 600; color: #333; }
    .items-header {
      display: flex;
      justify-content: space-between;
      font-size: ${baseFont - 2}px;
      font-weight: 900;
      text-transform: uppercase;
      padding: 4px 0;
      border-bottom: 2px solid #000;
      margin-bottom: 4px;
    }
    .item-name {
      font-size: ${baseFont}px;
      font-weight: 600;
      margin-top: 5px;
    }
    .item-code { font-size: ${baseFont - 3}px; font-weight: 600; color: #555; }
    .item-detail {
      display: flex;
      justify-content: space-between;
      font-size: ${baseFont - 1}px;
      font-weight: 600;
      padding-left: 8px;
    }
    .item-disc {
      padding-left: 8px;
      font-size: ${baseFont - 2}px;
      font-weight: 600;
      color: #B00020;
    }
    .total-section {
      background: #f0f0f0;
      padding: 6px 8px;
      margin: 0 -8px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      font-size: ${baseFont}px;
      font-weight: 700;
      margin: 3px 0;
    }
    .grand-total-section {
      background: #000;
      color: #fff;
      padding: 8px;
      margin: 0 -8px;
    }
    .grand-total-section.bold .grand-total { font-weight: 900; }
    .grand-total {
      display: flex;
      justify-content: space-between;
      font-size: ${baseFont + 7}px;
      font-weight: 700;
      letter-spacing: 1px;
    }
    .payment-section { padding: 6px 0; }
    .payment-title {
      font-size: ${baseFont - 2}px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }
    .pay-line {
      display: flex;
      justify-content: space-between;
      font-size: ${baseFont}px;
      font-weight: 700;
      margin: 2px 0;
    }
    .pay-line.change { font-weight: 900; font-size: ${baseFont + 1}px; }
    .balance-section {
      background: #f0f0f0;
      padding: 6px 8px;
      margin: 6px -8px 0;
    }
    .balance-title {
      font-size: ${baseFont - 2}px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }
    .balance-new {
      border-top: 2px solid #000;
      padding-top: 4px;
      margin-top: 4px;
      font-weight: 900;
      font-size: ${baseFont + 1}px;
    }
    .barcode-wrap { text-align: center; margin: 8px 0; }
    .barcode-wrap svg { width: 80%; max-width: 200px; height: 38px; }
    .barcode-text {
      font-family: 'Courier New', monospace;
      font-size: ${baseFont - 2}px;
      letter-spacing: 2px;
      margin-top: 2px;
    }
    .footer-section {
      text-align: center;
      padding: 8px 0 4px;
    }
    .footer-thanks {
      font-size: ${baseFont + 1}px;
      font-weight: 900;
      margin-bottom: 4px;
    }
    .footer-promo {
      font-size: ${baseFont - 1}px;
      font-weight: 700;
      margin-bottom: 3px;
    }
    .footer-policy {
      font-size: ${baseFont - 2}px;
      font-weight: 600;
      color: #444;
      margin-bottom: 4px;
      line-height: 1.4;
    }
    .footer-brand {
      font-size: ${baseFont - 3}px;
      font-weight: 600;
      color: #666;
    }
    .exchange-block {
      background: #FAF5FF;
      padding: 6px 8px;
      margin: 0 -8px;
      border-left: 3px solid #7C3AED;
    }
    .exchange-title {
      font-size: ${baseFont - 1}px;
      font-weight: 900;
      color: #5B21B6;
      margin-bottom: 4px;
    }
    .exchange-reason {
      font-size: ${baseFont - 2}px;
      font-weight: 600;
      color: #6B21A8;
      margin-bottom: 6px;
    }
    .return-name { color: #B45309; }
    .return-tag {
      background: #FED7AA; color: #92400E;
      padding: 1px 5px; border-radius: 3px;
      font-size: ${baseFont - 4}px;
      font-weight: 700;
      vertical-align: middle;
    }
    .exchange-line {
      border-top: 1px dashed #C4B5FD;
      padding-top: 4px;
      margin-top: 4px;
    }
    .exchange-net {
      display: flex; justify-content: space-between;
      margin-top: 6px;
      padding: 6px 0 0;
      border-top: 2px solid #7C3AED;
      font-size: ${baseFont + 2}px;
      font-weight: 900;
      color: #5B21B6;
    }
    @media print { body { width: ${paper.width}; } }
  </style>
</head>
<body>
  ${copiesHtml}
  <script>
    function printAll(){ try { window.print(); } catch(e) {} }
    window.onload = function(){ ${barcodeScript} };
  </script>
</body>
</html>`;

  // Sized to suit the chosen paper for the preview window. Actual print uses
  // @page rules above so the receipt prints at the right physical width.
  const winW = cfg.paperSize === 'A4' ? 760 : cfg.paperSize === 'A5' ? 520 : cfg.paperSize === '58mm' ? 280 : 350;
  const win = window.open('', '_blank', `width=${winW},height=620`);
  if (win) {
    win.document.write(html);
    win.document.close();
  }
};

export default printReceipt;
